/**
 * AppICEMAP — AuthHeaderWidget
 * Self-contained auth widget injected into app-header without modifying AppShell.
 * Shows login button when logged out, profile + notification badge when logged in.
 */
import { isAuthenticated } from "../../api/directus.js";
import router from "../../router.js";
import { state, subscribe } from "../../store/index.js";
import { getNotificationCount } from "../../services/push-notifications.js";

export class AuthHeaderWidget {
	static init() {
		// Wait for app-header to exist, then inject
		const inject = () => {
			const header = document.querySelector(".app-header");
			if (!header) return requestAnimationFrame(inject);

			// Avoid double-injection
			if (header.querySelector(".auth-header-widget")) return;

			const widget = document.createElement("div");
			widget.className = "auth-header-widget";
			widget.innerHTML = AuthHeaderWidget._render(isAuthenticated());
			header.appendChild(widget);

			AuthHeaderWidget._bindEvents(widget);
			AuthHeaderWidget._listenAuth(widget);
		};

		requestAnimationFrame(inject);
	}

	static _render(loggedIn) {
		const user = loggedIn ? state.user : null;
		if (loggedIn && user) {
			const initials = user.first_name
				? user.first_name.charAt(0).toUpperCase()
				: "U";
			const count = getNotificationCount();
			const badgeHtml =
				count > 0
					? `<span class="aw-badge" data-count="${count}">${count > 99 ? "99+" : count}</span>`
					: "";

			return `
        <div class="aw-user-area">
          <button class="aw-btn aw-btn--icon aw-btn--notif" aria-label="Notificaciones" id="aw-btn-notif">
            <i class="ph ph-bell" aria-hidden="true"></i>
            ${badgeHtml}
          </button>
          <button class="aw-btn aw-btn--avatar" aria-label="Perfil" id="aw-btn-profile">
            <span class="aw-avatar">${initials}</span>
          </button>
        </div>`;
		}

		return `
      <a class="aw-login-link" id="aw-btn-login" role="button" aria-label="Iniciar sesión">
        Iniciar sesión
      </a>`;
	}

	static _bindEvents(widget) {
		widget.addEventListener("click", (e) => {
			const target = e.target.closest("[id]");
			if (!target) return;

			switch (target.id) {
				case "aw-btn-login":
					router.navigate("/login");
					break;
				case "aw-btn-profile":
					router.navigate("/profile");
					break;
				case "aw-btn-notif":
					router.navigate("/notifications");
					break;
			}
		});
	}

	static _listenAuth(widget) {
		// React to auth changes
		subscribe("user", (user) => {
			widget.innerHTML = AuthHeaderWidget._render(!!user);
			AuthHeaderWidget._bindEvents(widget);
		});

		// React to notification count changes
		window.addEventListener("notifications:updated", () => {
			const badge = widget.querySelector(".aw-badge");
			const count = getNotificationCount();
			if (badge) {
				if (count > 0) {
					badge.setAttribute("data-count", String(count));
					badge.textContent = count > 99 ? "99+" : String(count);
				} else {
					badge.remove();
				}
			} else if (count > 0 && isAuthenticated()) {
				widget.innerHTML = AuthHeaderWidget._render(true);
				AuthHeaderWidget._bindEvents(widget);
			}
		});
	}
}
